"use client";
import React, { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementType } from "@/constants/tagular/main";
import { DEFAULT_USE_IN_VIEW_OPTIONS } from "@/constants/tagular/main";
import Icon from "@/components/icons";
import useTranslations from "@/i18n/useTranslations";

type ColumnData = {
  icon: string;
  percentage?: string;
  text?: string;
  description: string;
};

type UpskillingSurveyPromotionProps = {
  title?: string;
  subtitle?: string;
  columns: ColumnData[];
  ctaText?: string;
  ctaHref?: string;
};

const UpskillingSurveyPromotion = ({
  title,
  subtitle,
  columns,
  ctaText,
  ctaHref,
}: UpskillingSurveyPromotionProps) => {
  const t = useTranslations("Common");
  const { ref, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  // Component viewed event
  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Component,
      htmlId: "survey-promo-banner",
      location: "learn-ai-hub",
      text: title || "Upskilling Survey Promotion",
      name: "upskilling-survey-promotion",
      position: "1",
    },
  });

  // CTA clicked event
  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Entry,
      htmlId: "survey-promo-banner",
      location: "learn-ai-hub",
      text: ctaText || "Read more",
      name: ctaText?.toLowerCase().replace(/\s+/g, "-") || "read-more",
      position: "1",
    },
  });

  useEffect(() => {
    if (inView) {
      elementViewed();
    }
  }, [inView, elementViewed]);

  return (
    <div
      ref={ref}
      data-testid="upskilling-survey-promotion"
      className="fullwidth bg-putty-200 p-8 text-center"
    >
      <div className="mb-8">
        <h2 className="!text-[56px] font-black mb-4 italic text-[#00262B]">
          {title}
        </h2>
        <p className="text-xl text-gray-700">{subtitle}</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {columns.map((column, index) => (
            <div
              key={index}
              className={`text-left ${index > 0 ? "md:border-l border-[#E7E4DB] md:pl-8" : ""}`}
            >
              <div className="flex items-center space-x-3 mb-0">
                <div className="flex-shrink-0">
                  <Icon
                    type={column.icon}
                    svgProps={{
                      width: 56,
                      height: 56,
                      className: "text-primary-500",
                    }}
                  />
                </div>

                <div>
                  {column.percentage ? (
                    <span className="text-[56px] font-black mb-4 italic text-[#00262B]">
                      {column.percentage}
                    </span>
                  ) : (
                    <span className="text-[56px] font-black mb-4 italic text-[#00262B]">
                      {column.text}
                    </span>
                  )}
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed text-md mt-0">
                {column.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center space-x-5">
        <p className="text-lg text-gray-700 mb-0">
          {t("exploreSurveyFindings")}
        </p>
        <a
          href={ctaHref}
          onClick={() => elementClicked()}
          className="inline-block bg-primary-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors no-underline"
        >
          {ctaText || t("readMore")}
        </a>
      </div>
    </div>
  );
};

export default UpskillingSurveyPromotion;
